// src/pages/dashboard/branch/menu-items/components/tabs/BasicInformationTab.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiDollarSign, FiGlobe } from 'react-icons/fi';
import InputField from '../../../../../../../components/ui/InputField';
import { getCategoryName } from '../../../../../../../utils/categoryHelper';
import { Category } from '../../../../../../../utils/supabaseClient';
import { FormData, ArFormData } from '../AddMenuItemModal';

interface BasicInformationTabProps {
	formData: FormData;
	arFormData: ArFormData;
	categories: Category[];
	onFormDataChange: (data: FormData) => void;
	onArFormDataChange: (data: ArFormData) => void;
}

const BasicInformationTab: React.FC<BasicInformationTabProps> = ({
	formData,
	arFormData,
	categories,
	onFormDataChange,
	onArFormDataChange
}) => {
	const { t, i18n } = useTranslation();

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		onFormDataChange({
			...formData,
			[name]: value
		});
	};

	const handleArInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		onArFormDataChange({
			...arFormData,
			[name]: value
		});
	};

	return (
		<div className="space-y-6">
			{/* English Information */}
			<div className="space-y-4">
				<div className="flex items-center gap-2 mb-4">
					<FiGlobe className="w-5 h-5 text-blue-600" />
					<h4 className="text-md font-medium text-gray-900 dark:text-white">
						{t('menuItems.englishInformation')}
					</h4>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<InputField
						label={t('menuItems.name')}
						name="name"
						value={formData.name}
						onChange={handleInputChange}
						placeholder={t('menuItems.namePlaceholder')}
						required
					/>

					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							{t('menuItems.category')} *
						</label>
						<select
							name="categoryId"
							value={formData.categoryId}
							onChange={handleInputChange}
							required
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
						>
							<option value="">{t('menuItems.selectCategory')}</option>
							{categories.map((category) => (
								<option key={category.id} value={category.id}>
									{getCategoryName(category, i18n.language)}
								</option>
							))}
						</select>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<InputField
						label={t('menuItems.price')}
						name="price"
						type="number"
						min="0"
						step="0.01"
						value={formData.price}
						onChange={handleInputChange}
						placeholder="19.99"
						icon={<FiDollarSign />}
						required
					/>

					<InputField
						label={t('menuItems.discountedPrice')}
						name="discountedPrice"
						type="number"
						min="0"
						step="0.01"
						value={formData.discountedPrice}
						onChange={handleInputChange}
						placeholder="15.99"
						icon={<FiDollarSign />}
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('menuItems.description')}
					</label>
					<textarea
						name="description"
						rows={3}
						value={formData.description}
						onChange={handleInputChange}
						placeholder={t('menuItems.descriptionPlaceholder')}
						className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
					/>
				</div>
			</div>

			{/* Arabic Information */}
			<div className="space-y-4">
				<div className="flex items-center gap-2 mb-4">
					<FiGlobe className="w-5 h-5 text-green-600" />
					<h4 className="text-md font-medium text-gray-900 dark:text-white">
						{t('menuItems.arabicInformation')}
					</h4>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('menuItems.arabicName')}
					</label>
					<input
						type="text"
						name="name"
						value={arFormData.name}
						onChange={handleArInputChange}
						placeholder={t('menuItems.arabicNamePlaceholder')}
						className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
						dir="rtl"
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('menuItems.arabicDescription')}
					</label>
					<textarea
						name="description"
						rows={3}
						value={arFormData.description}
						onChange={handleArInputChange}
						placeholder={t('menuItems.arabicDescriptionPlaceholder')}
						className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
						dir="rtl"
					/>
				</div>
			</div>
		</div>
	);
};

export default BasicInformationTab;